/**
 * Promotion / relegation: Mid-Season → Final Sprint uses ⏫️ / ⏬️; other transitions use ⬆️ / ⬇️.
 *
 * Player leaderboards (per team, per viewed season):
 * - The API returns the log row where new_season_id = season you are viewing (unique per team).
 * - Example: Team A promoted S1→S2 only → on Season 2 leaderboard that row’s completed season is S1 → ⬆️/⬇️.
 * - Same team later promoted S2→S3 → on Season 3 leaderboard the row’s completed season is S2 → ⏫️/⏬️.
 * - If they were not promoted/relegated into the season you’re viewing, there is no arrow badge for that view.
 */

export function normalizeSeasonLabel(name) {
  if (name == null || name === '') return '';
  return String(name)
    .toLowerCase()
    .replace(/\u2013|\u2014/g, '-') // en dash, em dash → ASCII hyphen
    .replace(/\s+/g, ' ')
    .trim();
}

/** Completed + next season pair (admin Team Placement). */
export function getPromotionRelegationTransitionStyle(completedSeason, nextSeason) {
  const c = normalizeSeasonLabel(completedSeason?.name);
  const n = normalizeSeasonLabel(nextSeason?.name);
  if (!c || !n) return 'default';

  const completedIsMid =
    c.includes('mid-season') ||
    c.includes('mid season') ||
    (c.includes('mid') && (c.includes('season') || /\bseason\s*2\b/.test(c))) ||
    /\bseason\s*2\b/.test(c);

  const nextIsFinalSprint =
    n.includes('final') ||
    n.includes('sprint') ||
    /\bseason\s*3\b/.test(n);

  if (completedIsMid && nextIsFinalSprint) return 'mid_to_final';
  return 'default';
}

const DEFAULT_PR_ICONS = { promote: '⬆️', relegate: '⬇️' };
const MID_TO_FINAL_PR_ICONS = { promote: '⏫️', relegate: '⏬️' };

/**
 * Icons for one leaderboard row. Uses promotion_completed_season_name from the log row
 * (completed season) and the season name you are viewing (the “new” season for that row).
 */
export function getPromotionRelegationIconsForLeaderboardRow(
  promotionStatus,
  completedSeasonName,
  viewedSeasonName
) {
  if (promotionStatus !== 'promote' && promotionStatus !== 'relegate') {
    return DEFAULT_PR_ICONS;
  }
  if (completedSeasonName == null || completedSeasonName === '' || !viewedSeasonName) {
    return DEFAULT_PR_ICONS;
  }
  const style = getPromotionRelegationTransitionStyle(
    { name: completedSeasonName },
    { name: viewedSeasonName }
  );
  return style === 'mid_to_final' ? MID_TO_FINAL_PR_ICONS : DEFAULT_PR_ICONS;
}
